<template>
    <section>
        <titlebar :title-stack="['Orçamentos']">
            <template #right>
                <b-button
                    v-if="isInRole('prd-orcamentos-alterar')"
                    class="prd-new-budget-button"
                    type="is-primary"
                    icon-left="file-document-plus-outline"
                    rounded
                    tag="router-link"
                    :to="{ name: 'prd-orcamento-novo' }"
                    >Novo orçamento</b-button
                >
            </template>
        </titlebar>
        <div class="section prd-page">
            <b-message v-if="$route.query.pesquisa === 'pagamentos'" type="is-info" :closable="false">
                Pesquisa por data de pagamento cadastrada no orçamento. Essa data não confirma a quitação.
            </b-message>
            <div class="prd-toolbar">
                <b-field class="prd-search"
                    ><b-input v-model.trim="filtro.procurar" icon="magnify" placeholder="Número, cliente, empresa ou documento" @keyup.native.enter="buscar(1)"
                /></b-field>
                <b-dropdown v-model="filtro.status" multiple aria-role="list" class="prd-status-filter">
                    <template #trigger>
                        <b-button icon-left="format-list-checks" icon-right="menu-down">{{ statusFiltroDescricao }}</b-button>
                    </template>
                    <b-dropdown-item custom>
                        <b-button size="is-small" type="is-text" @click="selecionarTodosStatus">Todos os status</b-button>
                    </b-dropdown-item>
                    <b-dropdown-item v-for="item in statusOrcamento" :key="item.id" :value="item.id" aria-role="listitem">
                        <b-icon :icon="filtro.status.includes(item.id) ? 'checkbox-marked' : 'checkbox-blank-outline'" size="is-small" />
                        {{ item.nome }}
                    </b-dropdown-item>
                </b-dropdown>
                <b-button type="is-light" icon-left="filter-variant" @click="filtrosAbertos = !filtrosAbertos">Filtros</b-button>
                <b-button type="is-primary" icon-left="magnify" @click="buscar(1)">Buscar</b-button>
            </div>
            <transition name="fade"
                ><div v-if="filtrosAbertos" class="prd-filter-panel columns is-multiline">
                    <div class="column is-3">
                        <b-field label="Data inicial"><b-datepicker v-model="filtro.dataInicial" icon="calendar" trap-focus /></b-field>
                    </div>
                    <div class="column is-3">
                        <b-field label="Data final"><b-datepicker v-model="filtro.dataFinal" icon="calendar" trap-focus /></b-field>
                    </div>
                    <div class="column is-3">
                        <b-field label="Responsável"
                            ><b-select v-model="filtro.responsavelId" expanded
                                ><option :value="null">Todos</option>
                                <option v-for="r in responsaveis" :key="r.id" :value="r.id">{{ r.nome }}</option></b-select
                            ></b-field
                        >
                    </div>
                    <div class="column is-3">
                        <b-field label="Data de pagamento inicial"><b-input v-model="filtro.dataPagamentoInicial" type="date" icon="calendar" /></b-field>
                    </div>
                    <div class="column is-3">
                        <b-field label="Data de pagamento final"><b-input v-model="filtro.dataPagamentoFinal" type="date" icon="calendar" /></b-field>
                    </div>
                    <div class="column is-3">
                        <b-field label="Condição de pagamento"><b-select v-model="filtro.condicaoPagamentoId" expanded>
                            <option :value="null">Todas</option>
                            <option v-for="condicao in condicoes" :key="condicao.id" :value="condicao.id">{{ condicao.nome }}</option>
                        </b-select></b-field>
                    </div>
                    <div class="column is-3 is-flex is-align-items-flex-end">
                        <b-button type="is-text" icon-left="broom" @click="limpar">Limpar filtros</b-button>
                    </div>
                </div></transition
            >

            <div class="prd-table-card">
                <b-table
                    :data="resultado.itens || []"
                    :loading="loading"
                    paginated
                    backend-pagination
                    :total="resultado.total"
                    :per-page="filtro.porPagina"
                    :current-page.sync="filtro.pagina"
                    @page-change="buscar"
                    mobile-cards
                    hoverable
                    narrowed
                >
                    <b-table-column field="numero" label="Código" width="140" v-slot="props"
                        ><strong>{{ props.row.numero }}</strong
                        ><small class="prd-version">v{{ props.row.versao }}</small></b-table-column
                    >
                    <b-table-column field="dataHoraCadastro" label="Criado " width="180" v-slot="props">
                        {{ data(props.row.dataHoraCadastro) }}
                        <small class="is-size-7 is-block prd-subtext"><b>Responsável:</b> {{ props.row.responsavelNome }}</small>
                    </b-table-column>
                    <b-table-column field="clienteNome" label="Cliente" v-slot="props"
                        ><strong>{{ props.row.clienteNome }}</strong>
                        <div v-if="props.row.clienteContato" class="prd-client-contact">
                            <button
                                type="button"
                                class="prd-whatsapp-button"
                                title="Iniciar conversa no WhatsApp"
                                @click.stop="abrirWhatsapp(props.row)"
                            >
                                <b-icon icon="whatsapp" type="is-success" size="is-small" />
                            </button>
                            <small class="prd-subtext">{{ props.row.clienteContato }}</small>
                        </div>
                        <small v-else class="prd-subtext">Contato não informado</small></b-table-column
                    >

                    <b-table-column field="valorTotalComercial" label="Valor" numeric width="150" v-slot="props"
                        ><span v-if="podeVerComercial">{{ moeda(props.row.valorTotalComercial) }}</span
                        ><span v-else>—</span>
                        <small class="is-size-7 is-block prd-subtext"><b>Itens:</b> {{ props.row.quantidadeItens }}</small>
                        <small class="is-size-7 is-block prd-subtext"><b>Anexos:</b> {{ props.row.quantidadeAnexos }}</small></b-table-column
                    >
                    <b-table-column field="status" label="Status" width="240" v-slot="props">
                        <div class="prd-status-cell">
                            <prd-status-tag :status="props.row.status" tipo="orcamento" />
                            <b-dropdown
                                v-if="podeAlterarStatus(props.row)"
                                append-to-body
                                position="is-bottom-left"
                                aria-role="menu"
                                @change="solicitarAlteracaoStatus(props.row, $event)"
                            >
                                <template #trigger>
                                    <a
                                        href="#"
                                        class="prd-status-edit"
                                        title="Alterar status"
                                        aria-label="Alterar status do orçamento"
                                        @click.prevent
                                    >
                                        <b-icon
                                            :icon="statusAlterandoId === props.row.id ? 'loading' : 'pencil'"
                                            size="is-small"
                                            :custom-class="statusAlterandoId === props.row.id ? 'mdi-spin' : ''"
                                        />
                                    </a>
                                </template>
                                <b-dropdown-item
                                    v-for="item in statusAlteraveis(props.row)"
                                    :key="item.id"
                                    :value="item.id"
                                    :disabled="item.id === props.row.status"
                                    aria-role="menuitem"
                                >
                                    <b-icon :icon="item.icon" size="is-small" />
                                    <span>{{ item.nome }}</span>
                                </b-dropdown-item>
                            </b-dropdown>
                            <a
                                v-if="isInRole('prd-orcamentos-gerar-pdf')"
                                href="#"
                                class="prd-status-edit"
                                title="Visualizar e imprimir orçamento"
                                aria-label="Visualizar e imprimir orçamento"
                                @click.prevent.stop="imprimir(props.row)"
                            >
                                <b-icon icon="printer" size="is-small" />
                            </a>
                        </div>
                    </b-table-column>
                    <b-table-column field="dataPagamento" label="Data de Pagamento" width="115" v-slot="props">{{ data(props.row.dataPagamento) || 'Não informado' }}</b-table-column>
                    <b-table-column field="dataEntrega" label="Prazo de Entrega" width="125" v-slot="props">{{ data(props.row.dataEntrega) || 'Não informado' }}</b-table-column>
                    <b-table-column label="" width="55" v-slot="props"
                        ><b-button type="is-light" size="is-small" icon-left="chevron-right" tag="router-link" :to="rotaDetalhe(props.row)" title="Abrir"
                    /></b-table-column>
                    <template #empty
                        ><prd-empty-state title="Nenhum orçamento encontrado" description="Tente remover um filtro ou crie o primeiro orçamento."
                    /></template>
                </b-table>
            </div>
        </div>
    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'
import titlebar from '@/components/titlebar.vue'
import prdStatusTag from '@/components/prd/prdStatusTag.vue'
import prdEmptyState from '@/components/prd/prdEmptyState.vue'

const STATUS_PADRAO = [1, 2, 5, 7, 8]

export default {
    name: 'PrdOrcamentos',
    components: { titlebar, prdStatusTag, prdEmptyState },
    data() {
        const statusDaRota = Number(this.$route.query.status)
        return {
            loading: false,
            statusAlterandoId: null,
            filtrosAbertos: this.$route.query.pesquisa === 'pagamentos',
            resultado: { itens: [], total: 0 },
            responsaveis: [],
            condicoes: [],
            filtro: {
                procurar: String(this.$route.query.procurar || ''),
                status: this.$route.query.status && statusDaRota >= 1 && statusDaRota <= 9
                    ? [statusDaRota] : this.$route.query.todos === '1' ? [1, 2, 3, 4, 5, 6, 7, 8, 9] : [...STATUS_PADRAO],
                responsavelId: null,
                dataInicial: null,
                dataFinal: null,
                dataPagamentoInicial: String(this.$route.query.dataPagamentoInicial || ''),
                dataPagamentoFinal: String(this.$route.query.dataPagamentoFinal || ''),
                condicaoPagamentoId: Number(this.$route.query.condicaoPagamentoId) || null,
                pagina: 1,
                porPagina: 20,
            },
            statusOrcamento: [
                { id: 1, nome: 'Em Aberto', icon: 'refresh' },
                { id: 2, nome: 'Enviado', icon: 'send-check-outline' },
                { id: 3, nome: 'Aprovado', icon: 'check-circle-outline' },
                { id: 4, nome: 'Recusado', icon: 'close-circle-outline' },
                { id: 5, nome: 'Solicitação de alteração', icon: 'file-edit-outline' },
                { id: 6, nome: 'Cancelado', icon: 'cancel' },
                { id: 7, nome: 'Análise Produção', icon: 'clipboard-search-outline' },
                { id: 8, nome: 'Análise Produção Finalizada', icon: 'clipboard-check-outline' },
                { id: 9, nome: 'Aprovado Produção', icon: 'factory' },
            ],
        }
    },
    computed: {
        ...mapState(['unidadeId', 'unidades']),
        ...mapGetters(['isInRole']),
        unidadeNome() {
            const unidade = this.unidades?.find((item) => item.id == this.unidadeId)
            return unidade?.nome
        },
        podeVerComercial() {
            return this.isInRole('prd-orcamentos-consultar')
        },
        statusFiltroDescricao() {
            if (this.filtro.status.length === this.statusOrcamento.length) return 'Todos os status'
            if (!this.filtro.status.length) return 'Nenhum status'
            if (this.filtro.status.length === 1)
                return this.statusOrcamento.find((item) => item.id === this.filtro.status[0])?.nome || 'Status'
            return `${this.filtro.status.length} status selecionados`
        },
    },
    watch: {
        unidadeId() {
            this.iniciar()
        },
    },
    created() {
        this.iniciar()
    },
    methods: {
        async iniciar() {
            if (!this.unidadeId) return
            await Promise.all([this.buscar(1), this.carregarResponsaveis(), this.carregarCondicoes()])
        },
        async buscar(pagina) {
            this.filtro.pagina = pagina || 1
            if (!this.filtro.status.length) {
                this.resultado = { itens: [], total: 0 }
                return
            }
            this.loading = true
            try {
                const p = new URLSearchParams({ unidadeId: this.unidadeId, pagina: this.filtro.pagina, porPagina: this.filtro.porPagina })
                if (this.filtro.procurar) p.append('procurar', this.filtro.procurar)
                this.filtro.status.forEach((status) => p.append('status', status))
                if (this.filtro.responsavelId) p.append('responsavelId', this.filtro.responsavelId)
                if (this.filtro.dataInicial) p.append('dataInicial', this.iso(this.filtro.dataInicial))
                if (this.filtro.dataFinal) p.append('dataFinal', this.iso(this.filtro.dataFinal))
                if (this.filtro.dataPagamentoInicial) p.append('dataPagamentoInicial', this.filtro.dataPagamentoInicial)
                if (this.filtro.dataPagamentoFinal) p.append('dataPagamentoFinal', this.filtro.dataPagamentoFinal)
                if (this.filtro.condicaoPagamentoId) p.append('condicaoPagamentoId', this.filtro.condicaoPagamentoId)
                const r = await http.get(`/api/prd/orcamentos?${p}`)
                this.resultado = r.data
            } catch (e) {
                this.erro(e)
            } finally {
                this.loading = false
            }
        },
        async carregarResponsaveis() {
            try {
                const r = await http.get(`/api/prd/apoio/responsaveis?unidadeId=${this.unidadeId}`)
                this.responsaveis = r.data || []
            } catch (e) {
                this.erro(e)
            }
        },
        async carregarCondicoes() {
            try {
                const r = await http.get(`/api/prd/apoio/condicoes-pagamento?unidadeId=${this.unidadeId}`)
                this.condicoes = r.data || []
            } catch (e) {
                this.erro(e)
            }
        },
        limpar() {
            this.filtro = Object.assign(this.filtro, {
                procurar: '',
                status: this.statusOrcamento.map((item) => item.id),
                responsavelId: null,
                dataInicial: null,
                dataFinal: null,
                dataPagamentoInicial: '',
                dataPagamentoFinal: '',
                condicaoPagamentoId: null,
            })
            this.buscar(1)
        },
        selecionarTodosStatus() {
            this.filtro.status = this.statusOrcamento.map((item) => item.id)
        },
        podeAlterarStatus(row) {
            const podeControlarCliente = this.isInRole('prd-orcamentos-controlar-cliente') && row.dataHoraAprovacao == null
            const podeEnviarAnalise = this.isInRole('prd-orcamentos-enviar-producao') && [1, 8].includes(row.status)
            return podeControlarCliente || podeEnviarAnalise
        },
        statusAlteraveis(row) {
            const opcoes = this.isInRole('prd-orcamentos-controlar-cliente') && row.dataHoraAprovacao == null
                ? this.statusOrcamento.filter((item) => [1, 2, 3, 4, 5, 6].includes(item.id))
                : []
            if (this.isInRole('prd-orcamentos-enviar-producao') && [1, 8].includes(row.status))
                opcoes.push({ id: 7, nome: 'Enviar para Análise', icon: 'send-outline' })
            return opcoes
        },
        solicitarAlteracaoStatus(row, status) {
            if (!status || status === row.status) return
            if (status === 7) {
                this.enviarAnalise(row)
                return
            }
            if (status === 3) {
                this.aprovarProducao(row)
                return
            }
            if ([4, 6].includes(status)) {
                const nome = status === 4 ? 'recusa' : 'cancelamento'
                this.$buefy.dialog.prompt({
                    title: status === 4 ? 'Recusar orçamento' : 'Cancelar orçamento',
                    message: `Informe o motivo da ${nome}.`,
                    inputAttrs: { maxlength: 1000, required: true },
                    confirmText: 'Confirmar',
                    cancelText: 'Voltar',
                    type: status === 4 ? 'is-danger' : 'is-dark',
                    onConfirm: (motivo) => this.alterarStatus(row, status, motivo),
                })
                return
            }
            this.alterarStatus(row, status)
        },
        enviarAnalise(row) {
            this.$buefy.dialog.confirm({
                title: 'Confirmar ação',
                message: 'O orçamento será enviado para análise da produção. Deseja continuar?',
                confirmText: 'Confirmar',
                cancelText: 'Cancelar',
                type: 'is-primary',
                hasIcon: true,
                onConfirm: async () => {
                    this.statusAlterandoId = row.id
                    try {
                        await http.post(`/api/prd/orcamentos/${row.id}/enviar-analise?unidadeId=${this.unidadeId}`)
                        this.$buefy.toast.open({ message: 'Orçamento enviado para análise.', type: 'is-success' })
                        await this.buscar(this.filtro.pagina)
                    } catch (e) {
                        this.erro(e)
                    } finally {
                        this.statusAlterandoId = null
                    }
                },
            })
        },
        aprovarProducao(row) {
            this.$buefy.dialog.confirm({
                title: 'Confirmar ação',
                message: 'Confirma a aprovação do cliente e o envio para fabricação?',
                confirmText: 'Confirmar',
                cancelText: 'Cancelar',
                type: 'is-primary',
                hasIcon: true,
                onConfirm: async () => {
                    this.statusAlterandoId = row.id
                    try {
                        await http.post(`/api/prd/orcamentos/${row.id}/aprovar-producao?unidadeId=${this.unidadeId}`)
                        this.$buefy.toast.open({ message: 'Orçamento aprovado para produção.', type: 'is-success' })
                        await this.buscar(this.filtro.pagina)
                    } catch (e) {
                        this.erro(e)
                    } finally {
                        this.statusAlterandoId = null
                    }
                },
            })
        },
        async alterarStatus(row, status, motivo = null) {
            this.statusAlterandoId = row.id
            try {
                await http.put(`/api/prd/orcamentos/${row.id}/status?unidadeId=${this.unidadeId}`, { status, motivo })
                this.$buefy.toast.open({ message: 'Status do orçamento atualizado.', type: 'is-success' })
                await this.buscar(this.filtro.pagina)
            } catch (e) {
                this.erro(e)
            } finally {
                this.statusAlterandoId = null
            }
        },
        rotaDetalhe(row) {
            return { name: 'prd-orcamento', params: { id: row.id } }
        },
        imprimir(row) {
            const url = `/api/prd/orcamentos/${row.id}/pdf?unidadeId=${encodeURIComponent(this.unidadeId)}`
            window.open(url);
        },
        iso(v) {
            return new Date(v).toISOString().slice(0, 10)
        },
        data(v) {
            return v ? new Date(v).toLocaleDateString('pt-BR') : ''
        },
        moeda(v) {
            return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },
        abrirWhatsapp(orcamento) {
            const celular = String(orcamento.clienteContato || '').replace(/\D/g, '')
            if (celular.length < 10) return

            const numeroWhatsapp = celular.length <= 11 ? `55${celular}` : celular
            const origem = this.unidadeNome ? `Aqui é da ${this.unidadeNome}. ` : ''
            const mensagem = `Olá, ${orcamento.clienteNome}! ${origem}Estamos entrando em contato com você. Tudo bem?`
            const url = `https://wa.me/${numeroWhatsapp}?text=${encodeURIComponent(mensagem)}`

            window.open(url, '_blank', 'noopener,noreferrer')
        },
        erro(e) {
            this.$buefy.toast.open({ message: e.data?.erro || 'Não foi possível carregar os orçamentos.', type: 'is-danger' })
        },
    },
}
</script>

<style scoped>
.prd-new-budget-button.button {
    min-height: 2.65rem;
    padding: 0.65rem 1.2rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(135deg, var(--brand-teal-500), var(--brand-teal-600));
    box-shadow: 0 7px 16px rgba(22, 143, 138, 0.24);
    font-weight: 700;
    letter-spacing: 0.01em;
    transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;
}
.prd-new-budget-button.button:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(22, 143, 138, 0.3);
    filter: brightness(1.05);
}
.prd-new-budget-button.button:focus-visible {
    box-shadow: 0 0 0 3px rgba(22, 143, 138, 0.18), 0 8px 18px rgba(22, 143, 138, 0.26);
}
.prd-new-budget-button.button:active {
    transform: translateY(0);
}
.prd-page {
    background: var(--surface-page);
    min-height: calc(100vh - 110px);
    padding: 1rem 1rem !important;
}
.prd-toolbar {
    display: flex;
    gap: 0.6rem;
    align-items: flex-start;
    margin-bottom: 0.85rem;
}
.prd-search {
    flex: 1;
}
.prd-filter-panel {
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 12px;
    margin: 0 0 0.85rem;
    padding: 0.5rem;
}
.prd-table-card {
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 5px 18px rgba(31, 53, 78, 0.04);
}
.prd-version {
    display: inline-block;
    margin-left: 0.35rem;
    background: #edf2f7;
    color: #607085;
    border-radius: 5px;
    padding: 0.08rem 0.3rem;
}
.prd-subtext {
    display: block;
    color: #8492a6;
    margin-top: 0.15rem;
}
.prd-status-cell {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}
.prd-status-edit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #738296;
}
.prd-status-edit:hover,
.prd-status-edit:focus-visible {
    color: var(--brand-teal-600);
    outline: none;
}
.prd-client-contact {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    margin-top: 0.15rem;
}
.prd-client-contact .prd-subtext {
    margin-top: 0;
}
.prd-whatsapp-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
}
.prd-whatsapp-button:hover,
.prd-whatsapp-button:focus-visible {
    filter: brightness(0.85);
    outline: none;
}
@media (max-width: 768px) {
    .prd-page {
        padding: 1rem;
    }
    .prd-toolbar {
        flex-wrap: wrap;
    }
    .prd-search {
        flex-basis: 100%;
    }
}
</style>
